import React, { useEffect, useRef, useState } from 'react';
import { Link, navigate } from '../router';
import { useAuth, formatPrice, PLAN_NAMES } from '../auth';
import { PaidTier, Plan } from '../types/problem';
import { startCheckout } from '../billing';
import { SITE } from '../config';
import Icon from './Icon';
import { plural } from '../utils/format';

export function Brand({ to = '/' }: { to?: string }) {
  return (
    <Link to={to} className="brand" style={{ textDecoration: 'none' }}>
      <span className="brand-mark"><Icon name="logo" size={18} /></span>
      <span className="brand-name">{SITE.name}</span>
    </Link>
  );
}

export function UserMenu() {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const user = me?.user;
  if (!user) return null;
  const initial = (user.name || user.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="user-menu" ref={ref}>
      <button className="avatar" onClick={() => setOpen(o => !o)} aria-label="Account menu" aria-expanded={open}>
        {user.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" /> : initial}
      </button>
      {open && (
        <div className="menu card" role="menu">
          <div className="menu-head">
            <div>{user.name || 'Your account'}</div>
            <div>{user.email}</div>
          </div>
          <Link to="/app" onClick={() => setOpen(false)}><Icon name="grid" size={14} /> Dashboard</Link>
          <Link to="/app/settings" onClick={() => setOpen(false)}><Icon name="settings" size={14} /> Settings & billing</Link>
          <Link to="/extension" onClick={() => setOpen(false)}><Icon name="puzzle" size={14} /> Get the extension</Link>
          <button
            onClick={async () => {
              setOpen(false);
              await logout();
              navigate('/');
            }}
          >
            <Icon name="logout" size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function SiteHeader() {
  const { me } = useAuth();
  const authed = me?.authenticated;
  return (
    <header className="topbar site-header">
      <div className="topbar-inner">
        <Brand />
        <nav className="site-nav" aria-label="Main">
          <Link to="/#features" className="nav-item">Features</Link>
          <Link to="/pricing" className="nav-item">Pricing</Link>
          <Link to="/extension" className="nav-item">Extension</Link>
          {authed ? (
            <>
              <Link to="/app" className="btn btn-primary btn-sm">Open app</Link>
              <UserMenu />
            </>
          ) : (
            <>
              <Link to="/login" className="nav-item nav-keep">Sign in</Link>
              <Link to="/signup" className="btn btn-primary btn-sm">Get started</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div>
          <Brand />
          <p style={{ marginTop: 8 }}>Spaced repetition for the LeetCode problems you've solved.</p>
          <p style={{ marginTop: 4 }}>© {new Date().getFullYear()} {SITE.businessName}</p>
        </div>
        <nav className="footer-links" aria-label="Footer">
          <Link to="/pricing">Pricing</Link>
          <Link to="/extension">Extension</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/refunds">Refunds</Link>
          <Link to="/contact">Contact</Link>
        </nav>
      </div>
    </footer>
  );
}

export function SiteLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="site">
      <SiteHeader />
      <main className="site-main">{children}</main>
      <SiteFooter />
    </div>
  );
}

const FREE_FEATURES = (grades: number) => [
  'Unlimited saved problems',
  'Daily spaced-repetition queue',
  'Chrome extension with auto-capture',
  `${plural(grades, 'AI-graded answer')} every month`,
  'Unlimited self-graded reviews',
  'Export & import your data anytime',
];

const PAID_FEATURES: Record<PaidTier, (plan: Plan) => string[]> = {
  plus: plan => ['Everything in Free', `${plural(plan.monthlyGrades, 'AI-graded answer')} every month`, 'Support an indie project'],
  pro: () => ['Everything in Plus', 'Unlimited AI grading', 'Priority support'],
};

const PAID_DESC: Record<PaidTier, string> = {
  plus: 'For steady practice, a few problems a day.',
  pro: 'For daily grinders preparing for interviews.',
};

export function PricingCards() {
  const { me, config, setMe } = useAuth();
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');

  const current = me?.user?.plan ?? 'free';
  // Switching tiers means cancelling first, so an active subscription locks the other cards.
  const locked = current !== 'free' && me?.user?.subscriptionStatus === 'active';

  async function upgrade(plan: Plan) {
    setError('');
    if (!me?.authenticated) {
      navigate(`/signup?next=${encodeURIComponent('/pricing')}`);
      return;
    }
    setBusyKey(plan.key);
    try {
      const updated = await startCheckout(plan.key);
      if (updated) {
        setMe(updated);
        navigate('/app/settings?upgraded=1');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setBusyKey('');
    }
  }

  return (
    <>
      <div className="pricing">
        <div className="card price-card">
          <div className="price-name">Free</div>
          <div className="price-amount"><strong>₹0</strong><span>forever</span></div>
          <p className="price-desc">Everything you need to stop forgetting solutions.</p>
          <ul className="price-list">
            {FREE_FEATURES(config!.freeMonthlyGrades).map(f => <li key={f}><Icon name="check" size={16} />{f}</li>)}
          </ul>
          {me?.authenticated ? (
            <Link to="/app" className="btn btn-secondary btn-lg btn-block">{current === 'free' ? 'Your current plan' : 'Open app'}</Link>
          ) : (
            <Link to="/signup" className="btn btn-secondary btn-lg btn-block">Get started free</Link>
          )}
        </div>

        {config!.plans.map(plan => (
          <div key={plan.key} className={`card price-card ${plan.tier === 'plus' ? 'featured' : ''}`}>
            <div className="price-name">
              {PLAN_NAMES[plan.tier]} {plan.tier === 'plus' && <span className="badge badge-accent">Most popular</span>}
            </div>
            <div className="price-amount">
              <strong>{formatPrice(plan.amount)}</strong>
              <span>/ {plan.period === 'monthly' ? 'month' : 'year'}</span>
            </div>
            <p className="price-desc">{PAID_DESC[plan.tier]}</p>
            <ul className="price-list">
              {PAID_FEATURES[plan.tier](plan).map(f => <li key={f}><Icon name="check" size={16} />{f}</li>)}
            </ul>
            {current === plan.tier ? (
              <Link to="/app/settings" className="btn btn-primary btn-lg btn-block">Manage subscription</Link>
            ) : locked ? (
              <Link to="/app/settings" className="btn btn-secondary btn-lg btn-block">Change plan</Link>
            ) : (
              <button
                className="btn btn-primary btn-lg btn-block"
                onClick={() => upgrade(plan)}
                disabled={!!busyKey || (me?.authenticated && !config!.paymentsEnabled)}
              >
                {busyKey === plan.key && <span className="spinner" />}
                {me?.authenticated && !config!.paymentsEnabled ? 'Coming soon' : `Get ${PLAN_NAMES[plan.tier]}`}
              </button>
            )}
          </div>
        ))}
      </div>
      {error && <p className="error-text form-error">{error}</p>}
    </>
  );
}
